import React from 'react'
import IconWithBackground from '../IconWithBackground/IconWithBackground'
import { OrderDetailResponse, OrderLine, Product } from '../../model'
import { formatDate } from '../../helpers/utils'
import { formatCurrencyValue } from '../../helpers/utils'
import ProductItem from '../ProductItem/ProductItem'

interface OrderDetailHeaderProps {
    order: OrderDetailResponse
}

const OrderDetailHeader: React.FC<OrderDetailHeaderProps> = ({ order }) => {
    const address = order.address

    return (
        <div className='w-full'>
            <h2 className='text-base'>Opdracht</h2>
            <div className='w-full'>
                <div className='grid items-center lg:items-start flex-grow grid-cols-12 gap-y-2 gap-x-3 lg:gap-x-4 w-full'>
                    <div className='col-span-full sm:col-span-5 lg:col-span-5 flex items-center space-x-3'>
                        <div>
                            <div className='flex items-center space-x-3 w-full min-w-0'>
                                <IconWithBackground iconName='house' />
                                <div className='flex flex-col'>
                                    <span className='font-semibold text-sm truncate text-wrap'>
                                        {address.street} {address.houseNumber}
                                        {address.extension && ` (${address.extension})`}
                                    </span>
                                    <span className='text-xs font-medium text-[--tb-textLight]'>
                                        {address.zipcode} {address.city}
                                    </span>
                                    {order.surface && (
                                        <span className='text-xs text-[--tb-primary]'>{order.surface} m2</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='flex justify-between items-center my-2 sm:my-0 flex-wrap gap-4 col-span-full sm:col-span-5 lg:col-span-4'>
                        {order.pack ? (
                            <div className='w-full max-w-xs'>
                                <ProductItem
                                    product={order.pack}
                                    showOptions={false}
                                    size='small'
                                    orderDate={order.orderDate}
                                    className='px-0 py-0 border-0 !items-start'
                                />
                            </div>
                        ) : (
                            <div className='flex space-x-3 items-center'>
                                <IconWithBackground size='small' iconName={'calendar'} />

                                <div className='flex flex-col justify-center w-full min-w-0'>
                                    <span className='font-medium text-sm truncate text-wrap'>{order.title}</span>
                                    {order.orderDate && (
                                        <span>
                                            <strong className='mr-2 font-semibold'>Besteld:</strong>
                                            <span className='text-[--tb-textLight]'>{formatDate(order.orderDate)}</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Price */}
                    <div className='col-span-full sm:col-span-2 lg:col-span-3 flex flex-col justify-end text-sm text-left sm:text-right'>
                        <span className='text-[--tb-textLight] text-base'>Totaal</span>
                        {order.totalPrice ? (
                            <>
                                <span className='font-semibold'>{formatCurrencyValue(order.totalPrice)}</span>
                                <span className='text-xs text-[--tb-textLight]'> incl. producten en opties</span>
                            </>
                        ) : (
                            '-'
                        )}
                    </div>
                </div>
                {order.remarks && (
                    <div className='flex justify-start space-x-2 items-center mt-4 sm:pl-14 w-full'>
                        <IconWithBackground iconName='comment' size='extraSmall' className='mr-2' />
                        <span className='text-xs'>{order.remarks}</span>
                    </div>
                )}
            </div>
            <div className='flex w-full flex-col mt-4 border-top pt-2'>
                <h3 className='mb-1 mt-4'>Jouw producten</h3>

                <div className='relative w-full mx-auto'>
                    {order.products?.length > 5 && (
                        <div className='hidden md:block absolute pointer-events-none inset-y-0 left-1/2 w-px bg-[--tb-blushPrimary]' />
                    )}
                    <div
                        className={`${order.products?.length > 5 ? 'w-full md:auto-cols-fr  grid md:grid-rows-' + (order.products.length / 2).toFixed(0) + ' md:grid-flow-col' : 'max-w-md'} gap-4 gap-x-12`}
                    >
                        {(() => {
                            const products = order.products ?? []
                            const packed = products.filter((p: OrderLine) => p.isPartOfPack)
                            const others = products.filter((p: OrderLine) => !p.isPartOfPack)

                            return [...packed, ...others].map((product: OrderLine) => (
                                <ProductItem
                                    key={product.id}
                                    product={product as Product}
                                    showOptions
                                    size='medium'
                                    className='!p-0 !py-3 w-full !border-0 !items-start'
                                    iconLocation='start'
                                    colors={{
                                        iconColor: 'text-[--tb-primary]',
                                        iconBgColor: 'bg-[--tb-blushPrimary]'
                                    }}
                                    orderPackIcon={product.isPartOfPack ? 'box-taped' : undefined}
                                />
                            ))
                        })()}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OrderDetailHeader
